import { useState } from "react";
import { useMasters } from "../../contexts/MastersContext";
import SalaryGradeCombobox from "../UI/SalaryGradeCombobox";
import { useCategoryMaster } from "../../contexts/CategoryMasterContext";
import { useStateCode } from "../../contexts/StateCodesContext";
import { useEmployeeGrade } from "../../contexts/EmployeeGradesContext";
import { useSalaryBand } from "../../contexts/SalaryBandContext"; 

const EmploymentDetailForm = ({
  loading,
  closeModal,
  onSubmit,
  initialValues = {},
  errors = {},
}) => {
  const { departments, designations, employmentTypes, workplaces, shifts } =
    useMasters();
  const { categories } = useCategoryMaster();
  const { stateCodes } = useStateCode();
  const { employeeGrades } = useEmployeeGrade();
  const { salaryBands } = useSalaryBand();

  const [formData, setFormData] = useState({
    employee_id: "",
    designation: "",
    department: "",
    joining_date: "",
    shift: "",
    grade: "",
    branch: "",
    division: "",
    category: "",
    salary_grade: "",
    employment_type: "",
    leaving_date: "",
    state_code: "",
    status: "",
    pf_uan: "",
    esic_no: "",
    resignation_date: "",
    notice_period_days: "",
    probation_period_days: "",
    ...initialValues,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    // List of dropdowns that should store IDs as numbers
    const fieldsRequiringId = [
      "department",
      "designation",
      "state_code",
      "grade",
      "branch",
      "category",
      "shift",
      "employment_type",
      "salary_grade"
    ];

    setFormData((prev) => ({
      ...prev,
      [name]: fieldsRequiringId.includes(name)
        ? Number(value) || "" // store as number for IDs, empty string if NaN
        : value.trimStart() // trim only for text fields
    }));
  };

  // Helper function to get ID from name for initial values
  const getIdFromName = (name, value, dataArray, keyField = 'name') => {
    if (!value) return "";
    // If it's already a number (ID), return it
    if (typeof value === 'number') return value;
    // If it's a string (name), find the corresponding ID
    const item = dataArray.find(item => item[keyField] === value);
    return item ? item.id : "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const divisionRegex = /^[A-Za-z\s]+$/;
    if (!divisionRegex.test(formData.division)) {
      alert("Division must contain only letters and spaces.");
      return;
    }

    if (!formData.joining_date) {
      alert("Joining Date is required and must be valid.");
      return;
    }

    const joiningDate = new Date(formData.joining_date);
    const optionalDates = [
      { key: "resignation_date", label: "Resignation Date" },
      { key: "leaving_date", label: "Leaving Date" },
    ];

    for (const field of optionalDates) {
      const fieldValue = formData[field.key];
      if (fieldValue) {
        const dateValue = new Date(fieldValue);
        if (dateValue <= joiningDate) {
          alert(`${field.label} must be after Joining Date.`);
          return;
        }
      }
    }

    const uanRegex = /^\d{12}$/;
    if (formData.pf_uan && !uanRegex.test(formData.pf_uan)) {
      alert("PF/UAN must be exactly 12 digits.");
      return;
    }

    const esicRegex = /^\d{10}$/;
    if (formData.esic_no && !esicRegex.test(formData.esic_no)) {
      alert("ESIC No. must be exactly 10 digits.");
      return;
    }

    // Helper function to get ID from name for submission
    const getIdForSubmission = (value, dataArray, keyField = 'name') => {
      if (!value) return null;
      // If it's already a number (ID), return it
      if (typeof value === 'number') return value;
      // If it's a string (name), find the corresponding ID
      const item = dataArray.find(item => item[keyField] === value);
      return item ? item.id : null;
    };

    // Prepare submission data with proper types - convert names to IDs
    const submissionData = {
      employee_id: formData.employee_id,
      designation: getIdForSubmission(formData.designation, designations),
      department: getIdForSubmission(formData.department, departments),
      joining_date: formData.joining_date,
      shift: getIdForSubmission(formData.shift, shifts),
      grade: getIdForSubmission(formData.grade, employeeGrades),
      branch: getIdForSubmission(formData.branch, workplaces),
      division: formData.division,
      category: getIdForSubmission(formData.category, categories),
      salary_grade: getIdForSubmission(formData.salary_grade, salaryBands),
      employment_type: getIdForSubmission(formData.employment_type, employmentTypes),
      leaving_date: formData.leaving_date || null,
      state_code: getIdForSubmission(formData.state_code, stateCodes, 'code'),
      status: formData.status,
      pf_uan: formData.pf_uan,
      esic_no: formData.esic_no,
      resignation_date: formData.resignation_date || null,
      notice_period_days: formData.notice_period_days ? Number(formData.notice_period_days) : null,
      probation_period_days: formData.probation_period_days ? Number(formData.probation_period_days) : null,
    };

    // Remove null values to avoid sending them
    const cleanedData = Object.fromEntries(
      Object.entries(submissionData).filter(([_, value]) => value !== null && value !== "")
    );

    onSubmit(cleanedData);
  };

  const renderInput = (label, name, type = "text", showAsterisk = false, is_required = false) => (
    <div>
      <label htmlFor={name} className="block dark:text-white text-sm font-medium text-gray-700">
        {label} 
        {showAsterisk && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type={type}
        id={name}
        name={name}
        value={formData[name] || ""}
        onChange={handleChange}
        className={`mt-1 block w-full border rounded-md dark:text-white dark:bg-gray-900 p-2 ${
          errors[name] ? "border-red-500" : "border-gray-300"
        }`}
        required={is_required} 
        {...(type === "number" ? { min: 0 } : {})}
      />
      {errors[name] && <p className="text-red-500 text-sm mt-1">{errors[name]}</p>}
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 font-poppins">
        {/* Disabled/Non-editable Employee ID Field */}
        <div className="">
          <label htmlFor="employee_id" className="block text-sm font-medium text-gray-700 dark:text-white">
            Employee ID <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            readOnly
            id="employee_id"
            name="employee_id"
            value={formData.employee_id}
            onChange={handleChange}
            disabled
            className="mt-1 block w-full cursor-not-allowed dark:bg-gray-900 dark:text-white dark:border-gray-700 border border-gray-300 rounded-md p-2 bg-gray-200"
          />
        </div>

        {/* Department field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Department <span className="text-red-500">*</span>
          </label>
          <select
            name="department"
            value={getIdFromName("department", formData.department, departments)}
            onChange={handleChange}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:text-white"
            required
          >
            <option value="">Select Department</option>
            {departments.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* designation field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Designation <span className="text-red-500">*</span>
          </label>
          <select
            name="designation"
            value={getIdFromName("designation", formData.designation, designations)}
            onChange={handleChange}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:text-white"
            required
          >
            <option value="">Select Designation</option>
            {designations.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* joining date field */}
        {renderInput("Joining Date", "joining_date", "date" , true, true)}

        {/* State code field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            State Code <span className="text-red-500">*</span>
          </label>
          <select
            name="state_code"
            value={getIdFromName("state_code", formData.state_code, stateCodes, 'code')}
            onChange={handleChange}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:text-white"
            required
          >
            <option value="">Select state code</option>
            {stateCodes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.code}
              </option>
            ))}
          </select>
        </div>
        
        {/* PF/UAN, ESIC, Probation period, notice period fields */}
        {renderInput("PF/UAN", "pf_uan", "number")}
        {renderInput("ESIC No", "esic_no", "number")}
        {renderInput("Probation Period (Days)", "probation_period_days", "number", true, true)}
        {renderInput("Notice Period (Days)", "notice_period_days", "number", true, true)}
        
        {/* employee grade field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Employee Grade <span className="text-red-500">*</span>
          </label>
          <select
            name="grade"
            value={getIdFromName("grade", formData.grade, employeeGrades)}
            onChange={handleChange}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:text-white"
            required
          >
            <option value="">Select Grade</option>
            {employeeGrades.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* branch field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Branch <span className="text-red-500">*</span>
          </label>
          <select
            name="branch"
            value={getIdFromName("branch", formData.branch, workplaces)}
            onChange={handleChange}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:text-white"
            required
          >
            <option value="">Select Branch</option>
            {workplaces.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* Division field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Division <span className="text-red-500">*</span>
          </label>
          <select
            name="division"
            value={formData.division}
            onChange={handleChange}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:text-white"
            required>
            <option value="">Select Division</option>
            <option value="Skilled">Skilled</option>
            <option value="UnSkilled">UnSkilled</option>
          </select>
        </div>

        {/* category field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            name="category"
            value={getIdFromName("category", formData.category, categories)}
            onChange={handleChange}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:text-white"
            required
          >
            <option value="">Select Category</option>
            {categories.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* shift field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Shift <span className="text-red-500">*</span>
          </label>
          <select
            name="shift"
            value={getIdFromName("shift", formData.shift, shifts)}
            onChange={handleChange}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:text-white"
            required
          >
            <option value="">Select shift</option>
            {shifts.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* salary grade field */}
        <SalaryGradeCombobox
          value={formData.salary_grade}
          onChange={(val) =>
            setFormData((prev) => ({ ...prev, salary_grade: val }))
          }
        />

        {/* employment type field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Employment Type <span className="text-red-500">*</span>
          </label>
          <select
            name="employment_type"
            value={getIdFromName("employment_type", formData.employment_type, employmentTypes)}
            onChange={handleChange}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:text-white"
            required
          >
            <option value="">Select employment type</option>
            {employmentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Form Action Buttons */}
      <div className="flex justify-end gap-2 items-center">
        <button
          type="button"
          onClick={closeModal}
          className="px-7 py-2 font-medium text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex items-center gap-2 px-7 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {initialValues.employee_id ? "Update" : "Add"}
          {loading && (
            <svg
              className="animate-spin h-5 w-5 ml-2 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          )}
        </button>
      </div>
    </form>
  );
};

export default EmploymentDetailForm;